import React, { useCallback, useEffect, useRef, useState } from "react";
import { selectedCompanyHasProfile } from "../utils/activityProfiles";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Empty,
  Grid,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MinusSquareOutlined,
  PlusSquareOutlined,
  ReloadOutlined,
  RocketOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  getRestaurantKitchenQueue,
  markRestaurantKitchenTicketReady,
  updateRestaurantOrderStatus,
} from "./api";
import { getKitchenActions } from "../utils/restaurantWorkflow";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import { playBusinessAudioAlert } from "../utils/restaurantAudio";
import BusinessAudioToggle from "../components/BusinessAudioToggle";
import "../gestion-produits/styles.css";
import "./ProductionKitchen.css";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const KITCHEN_STATUS_OPTIONS = [
  { value: "active", label: "À traiter en cuisine" },
  { value: "ready_to_serve", label: "Prêts à servir" },
  { value: "all", label: "Tous les tickets" },
  { value: "ready_for_production", label: "Convertibles" },
  { value: "blocked", label: "Bloques" },
  { value: "converted", label: "Préparations effectuées" },
  { value: "pending", label: "En attente" },
  { value: "not_required", label: "Hors production" },
  { value: "cancelled", label: "Annules" },
];

const KITCHEN_STATUS_META = {
  pending: { color: "default", label: "En attente" },
  ready_for_production: { color: "blue", label: "Convertible" },
  blocked: { color: "red", label: "Bloque" },
  not_required: { color: "default", label: "Hors production" },
  converted: { color: "blue", label: "Préparation effectuée" },
  ready_to_serve: { color: "green", label: "Prêt à servir" },
  cancelled: { color: "orange", label: "Annule" },
};

const SERVICE_MODE_LABELS = {
  dine_in: "Sur place",
  takeaway: "A emporter",
  delivery: "Livraison",
};

const KITCHEN_AUTO_REFRESH_INTERVAL_MS = 10000;
const KITCHEN_BATCH_MAX_ITEMS = 25;

const formatDateTime = (value) => {
  if (!value) {
    return "-";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString("fr-FR");
};

const formatQuantity = (value, unitShortName) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "-";
  }

  const renderedQuantity =
    numericValue % 1 === 0 ? numericValue.toString() : numericValue.toFixed(2);
  return unitShortName
    ? `${renderedQuantity} ${unitShortName}`
    : renderedQuantity;
};

const getServiceModeLabel = (value) =>
  SERVICE_MODE_LABELS[value] || value || "-";

const buildStatusTag = (status) => {
  const meta = KITCHEN_STATUS_META[status] || {
    color: "default",
    label: status || "Inconnu",
  };

  return <Tag color={meta.color}>{meta.label}</Tag>;
};

const buildDetailDescriptionSpan = (isMobileLayout) => (isMobileLayout ? 1 : 2);

const ProductionKitchen = () => {
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const screens = useBreakpoint();
  const isMobileLayout = !screens.md;

  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");
  const [searchText, setSearchText] = useState("");
  const [detailTicket, setDetailTicket] = useState(null);
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const hasNotificationBaselineRef = useRef(false);
  const previousTicketIdsRef = useRef(new Set());

  const isRestaurantActivity = selectedCompanyHasProfile(
    user,
    selectedCompany,
    "restaurant"
  );
  const canUpdateTicket = hasPermission(
    "Gestion Commerciale.Restauration.Cuisine.update_status"
  );
  const canConvertTicket =
    hasPermission(
      "Gestion Commerciale.Restauration.Cuisine.convert_to_production"
    ) ||
    hasPermission("Gestion Commerciale.Approvisionnement.Production.Gerer.create");
  const ticketActions = (ticket) => getKitchenActions(ticket, {
    canConvert: canConvertTicket,
    canUpdate: canUpdateTicket,
  });

  const loadQueue = useCallback(
    async ({
      nextStatus = statusFilter,
      nextSearch = searchText,
      silent = false,
    } = {}) => {
      if (!selectedWarehouse || !selectedCompany || !isRestaurantActivity) {
        setTickets([]);
        return;
      }

      if (!silent) {
        setLoading(true);
      }
      try {
        const params = {
          warehouse_id: selectedWarehouse,
          company_id: selectedCompany,
          page: 1,
          limit: 100,
        };

        if (nextStatus && nextStatus !== "all") {
          params.status = nextStatus;
        }

        if (String(nextSearch || "").trim()) {
          params.search = String(nextSearch).trim();
        }

        const data = await getRestaurantKitchenQueue(params);
        const nextTickets = Array.isArray(data?.tickets) ? data.tickets : [];
        const nextTicketIds = new Set(
          nextTickets
            .map((ticket) => Number(ticket?.id))
            .filter((ticketId) => Number.isFinite(ticketId) && ticketId > 0)
        );

        if (hasNotificationBaselineRef.current) {
          const hasNewTicket = Array.from(nextTicketIds).some(
            (ticketId) => !previousTicketIdsRef.current.has(ticketId)
          );

          if (hasNewTicket) {
            void playBusinessAudioAlert("kitchen_new_order");
          }
        } else {
          hasNotificationBaselineRef.current = true;
        }

        previousTicketIdsRef.current = nextTicketIds;
        setTickets(nextTickets);
        setSelectedTicketIds((current) =>
          current.filter((ticketId) =>
            nextTickets.some(
              (ticket) =>
                Number(ticket.id) === Number(ticketId) &&
                getKitchenActions(ticket, { canConvert: canConvertTicket }).canProduce,
            ),
          ),
        );
      } catch (error) {
        console.error("Erreur lors du chargement de la file cuisine:", error);
        if (!silent) {
          message.error(
            error?.error ||
              error?.message ||
              "Erreur lors du chargement de la file cuisine"
          );
          setTickets([]);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [
      canConvertTicket,
      isRestaurantActivity,
      searchText,
      selectedCompany,
      selectedWarehouse,
      statusFilter,
    ]
  );

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    hasNotificationBaselineRef.current = false;
    previousTicketIdsRef.current = new Set();
  }, [selectedCompany, selectedWarehouse, statusFilter, searchText]);

  useEffect(() => {
    if (!selectedWarehouse || !selectedCompany || !isRestaurantActivity) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      loadQueue({ silent: true });
    }, KITCHEN_AUTO_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isRestaurantActivity, loadQueue, selectedCompany, selectedWarehouse]);

  const handleOpenKitchenTicket = (ticket) => {
    if (!ticket?.id || !ticket?.production_unit_id) {
      message.error(
        "Ce ticket ne reference pas encore d'unite de production exploitable."
      );
      return;
    }

    const outputQuantity =
      Number(ticket?.eligible_quantity) ||
      Number(ticket?.requested_quantity) ||
      Number(ticket?.line_quantity) ||
      0;

    const params = new URLSearchParams();
    params.set("restaurant_ticket_id", String(ticket.id));
    params.set("production_unit_id", String(ticket.production_unit_id));

    if (outputQuantity > 0) {
      params.set("output_quantity", String(outputQuantity));
      params.set(
        "requested_quantity",
        String(Number(ticket?.requested_quantity) || outputQuantity)
      );
      params.set(
        "eligible_quantity",
        String(Number(ticket?.eligible_quantity) || outputQuantity)
      );
    }

    if (ticket?.status) {
      params.set("status", String(ticket.status));
    }

    navigate(
      {
        pathname: "/production/kitchen/process",
        search: params.toString(),
      },
      {
        state: {
          restaurantTicket: ticket,
        },
      }
    );
  };

  const handleOpenKitchenBatch = () => {
    const selectedTickets = tickets.filter((ticket) =>
      selectedTicketIds.includes(Number(ticket.id)) && ticketActions(ticket).canProduce,
    );
    if (selectedTickets.length === 0) {
      message.warning("Selectionnez au moins un ticket convertible.");
      return;
    }

    navigate("/production/kitchen/batch", {
      state: {
        initialItems: selectedTickets.map((ticket) => ({
          client_line_id: `ticket-${ticket.id}`,
          production_unit_id: Number(ticket.production_unit_id),
          output_quantity:
            Number(ticket.eligible_quantity) ||
            Number(ticket.requested_quantity) ||
            Number(ticket.line_quantity) ||
            1,
          restaurant_ticket_id: Number(ticket.id),
          source_label:
            ticket.product_name ||
            ticket.production_unit_name ||
            `Ticket #${ticket.id}`,
        })),
      },
    });
  };

  const toggleTicketSelection = (ticketId) => {
    const normalizedId = Number(ticketId);
    setSelectedTicketIds((current) => {
      if (current.includes(normalizedId)) {
        return current.filter((id) => id !== normalizedId);
      }
      if (current.length >= KITCHEN_BATCH_MAX_ITEMS) {
        message.warning(
          `Un lot est limite a ${KITCHEN_BATCH_MAX_ITEMS} tickets.`,
        );
        return current;
      }
      return [...current, normalizedId];
    });
  };

  const openDetailModal = (ticket) => {
    setDetailTicket(ticket || null);
  };

  const closeDetailModal = () => {
    setDetailTicket(null);
  };

  const detailTicketTitle = detailTicket
    ? `Details ticket ${detailTicket.order_number || `#${detailTicket.id}`}`
    : "Details ticket";

  const detailTicketContent = detailTicket ? (
    <div className="production-kitchen__details-modal">
      <Descriptions
        bordered
        size="small"
        column={isMobileLayout ? 1 : 2}
        layout={isMobileLayout ? "vertical" : "horizontal"}
        className="production-kitchen__details-descriptions"
      >
        <Descriptions.Item label="Date">
          {formatDateTime(detailTicket.order_date)}
        </Descriptions.Item>
        <Descriptions.Item label="Commande">
          {detailTicket.order_number || `Ticket #${detailTicket.id}`}
        </Descriptions.Item>
        <Descriptions.Item label="Table">
          {detailTicket.table_label || "Sans table"}
        </Descriptions.Item>
        <Descriptions.Item label="Service">
          {getServiceModeLabel(detailTicket.service_mode)}
        </Descriptions.Item>
        <Descriptions.Item label="Serveur">
          {detailTicket.waiter_name || "Non renseigne"}
        </Descriptions.Item>
        <Descriptions.Item label="Statut">
          {buildStatusTag(detailTicket.line_status === "ready" ? "ready_to_serve" : detailTicket.status)}
        </Descriptions.Item>
        <Descriptions.Item label="Article">
          {detailTicket.product_name || "Produit"}
        </Descriptions.Item>
        <Descriptions.Item label="Course">
          {detailTicket.course_label || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Quantite demandee">
          {formatQuantity(
            detailTicket.requested_quantity,
            detailTicket.unit_short_name
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Quantite eligible">
          {formatQuantity(
            detailTicket.eligible_quantity,
            detailTicket.unit_short_name
          )}
        </Descriptions.Item>
        <Descriptions.Item
          label="Unite cible"
          span={buildDetailDescriptionSpan(isMobileLayout)}
        >
          {detailTicket.production_unit_name || "Aucune unite liee"}
        </Descriptions.Item>
        <Descriptions.Item
          label="Note de ligne"
          span={buildDetailDescriptionSpan(isMobileLayout)}
        >
          {detailTicket.line_notes || "Aucune note de ligne."}
        </Descriptions.Item>
        <Descriptions.Item
          label="Motif de resolution"
          span={buildDetailDescriptionSpan(isMobileLayout)}
        >
          {detailTicket.resolution_reason || "Aucun motif de resolution."}
        </Descriptions.Item>
      </Descriptions>
    </div>
  ) : null;

  const handleMarkReady = async (ticket) => {
    setActionLoadingId(ticket.id);
    try {
      await markRestaurantKitchenTicketReady(ticket.id, { company_id: selectedCompany });
      message.success("Article prêt à servir. La salle est informée.");
      closeDetailModal();
      await loadQueue();
    } catch (error) {
      message.error(error?.details || error?.error || error?.message || "Validation cuisine impossible.");
    } finally { setActionLoadingId(null); }
  };

  const handleCancelOrder = (ticket) => Modal.confirm({
    title: "Annuler toute la commande ?",
    content: `Tous les articles de la commande ${ticket.order_number} seront annulés, y compris ceux qui ne sont pas affichés dans ce filtre.`,
    okText: "Annuler la commande", cancelText: "Conserver", okButtonProps: { danger: true },
    onOk: async () => {
      setActionLoadingId(ticket.id);
      try {
        await updateRestaurantOrderStatus(ticket.restaurant_order_id, { company_id: selectedCompany, flow_status: "cancelled" });
        message.success("Commande annulée.");
        closeDetailModal();
        await loadQueue();
      } catch (error) {
        message.error(error?.details || error?.error || error?.message || "Annulation impossible.");
        throw error;
      } finally { setActionLoadingId(null); }
    },
  });

  const renderKitchenActions = (record) => {
    const actions = ticketActions(record);
    const buttonProps = {
      size: isMobileLayout ? "middle" : "small",
      className: isMobileLayout ? "production-kitchen__mobile-action" : undefined,
    };
    const content = (
      <>
        <Tooltip title="Voir le détail">
          <Button {...buttonProps} aria-label="Voir le détail" icon={<EyeOutlined />}
            onClick={() => openDetailModal(record)}>
            {isMobileLayout ? "Détails" : null}
          </Button>
        </Tooltip>
        {actions.canProduce && (
          <Tooltip title="Lancer la préparation">
            <Button {...buttonProps} aria-label="Lancer la préparation" icon={<RocketOutlined />}
              type="primary" disabled={actionLoadingId !== null}
              onClick={() => handleOpenKitchenTicket(record)}>
              {isMobileLayout ? "Production" : null}
            </Button>
          </Tooltip>
        )}
        {actions.canMarkReady && (
          <Tooltip title="Valider cet article prêt à servir">
            <Button {...buttonProps} icon={<CheckCircleOutlined />} type="primary"
              loading={actionLoadingId === record.id} disabled={actionLoadingId !== null}
              onClick={() => handleMarkReady(record)}>
              Prêt à servir
            </Button>
          </Tooltip>
        )}
        {actions.canCancel && (
          <Tooltip title="Annuler toute la commande">
            <Button {...buttonProps} aria-label="Annuler toute la commande" danger icon={<StopOutlined />}
              disabled={actionLoadingId !== null} onClick={() => handleCancelOrder(record)}>
              {isMobileLayout ? "Annuler la commande" : null}
            </Button>
          </Tooltip>
        )}
      </>
    );
    return isMobileLayout ? content : <Space size="small" wrap>{content}</Space>;
  };

  const desktopExpandable = {
    expandedRowRender: (record) => (
      <div className="production-kitchen__expanded-panel">
        <div className="production-kitchen__expanded-grid">
          <div className="production-kitchen__expanded-item">
            <span>Quantite demandee</span>
            <strong>
              {formatQuantity(record.requested_quantity, record.unit_short_name)}
            </strong>
          </div>
          <div className="production-kitchen__expanded-item">
            <span>Quantite eligible</span>
            <strong>
              {formatQuantity(record.eligible_quantity, record.unit_short_name)}
            </strong>
          </div>
          <div className="production-kitchen__expanded-item">
            <span>Unite cible</span>
            <strong>{record.production_unit_name || "Aucune unite liee"}</strong>
          </div>
          <div className="production-kitchen__expanded-item">
            <span>Course</span>
            <strong>{record.course_label || "-"}</strong>
          </div>
        </div>
        {record.line_notes ? (
          <div className="production-kitchen__expanded-note">
            <strong>Note de ligne</strong>
            <span>{record.line_notes}</span>
          </div>
        ) : null}
        {record.resolution_reason ? (
          <div className="production-kitchen__expanded-note production-kitchen__expanded-note--warning">
            <strong>Motif de resolution</strong>
            <span>{record.resolution_reason}</span>
          </div>
        ) : null}
      </div>
    ),
    rowExpandable: () => true,
    expandIcon: ({ expanded, onExpand, record }) => (
      <Button
        type="text"
        size="small"
        icon={expanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
        onClick={(event) => onExpand(record, event)}
        className="production-kitchen__expand-icon"
        aria-label={expanded ? "Replier" : "Deplier"}
      />
    ),
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "order_date",
      key: "order_date",
      width: 180,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Commande",
      key: "order",
      width: 170,
      render: (_, record) => (
        <div className="production-kitchen__cell-stack">
          <Text strong className="production-kitchen__cell-title">
            {record.order_number || `Ticket #${record.id}`}
          </Text>
          <Text type="secondary">{record.table_label || "Sans table"}</Text>
        </div>
      ),
    },
    {
      title: "Service",
      dataIndex: "service_mode",
      key: "service_mode",
      width: 130,
      render: (value) => getServiceModeLabel(value),
    },
    {
      title: "Serveur",
      dataIndex: "waiter_name",
      key: "waiter_name",
      width: 150,
      render: (value) => value || "Non renseigne",
    },
    {
      title: "Article",
      key: "product",
      width: 220,
      render: (_, record) => (
        <div className="production-kitchen__cell-stack">
          <Text strong className="production-kitchen__cell-title">
            {record.product_name || "Produit"}
          </Text>
          <Text type="secondary">{record.course_label || "Sans course"}</Text>
        </div>
      ),
    },
    {
      title: "Quantite",
      key: "quantities",
      width: 180,
      render: (_, record) => (
        <div className="production-kitchen__cell-stack">
          <Text>
            Demande:{" "}
            {formatQuantity(record.requested_quantity, record.unit_short_name)}
          </Text>
          <Text type="secondary">
            Eligible:{" "}
            {formatQuantity(record.eligible_quantity, record.unit_short_name)}
          </Text>
        </div>
      ),
    },
    {
      title: "Unite cible",
      key: "production_unit",
      width: 180,
      render: (_, record) =>
        record.production_unit_name ? (
          <Text strong>{record.production_unit_name}</Text>
        ) : (
          <Text type="secondary">Aucune unite liee</Text>
        ),
    },
    {
      title: "Suivi",
      key: "status",
      width: 240,
      render: (_, record) => (
        <div className="production-kitchen__cell-stack">
          <div className="production-kitchen__status-stack">
            {buildStatusTag(record.line_status === "ready" ? "ready_to_serve" : record.status)}
          </div>
          <Text type="secondary">
            {record.resolution_reason || "Ticket pret pour la lecture cuisine."}
          </Text>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 270,
      align: "right",
      render: (_, record) => renderKitchenActions(record),
    },
  ];

  if (!isRestaurantActivity) {
    return (
      <Card>
        <Title level={3}>Cuisine</Title>
        <Alert
          type="warning"
          showIcon
          message="Module reserve a la restauration"
          description="La file cuisine est visible uniquement pour les entreprises dont le profil d'activite est positionne sur Restauration."
        />
      </Card>
    );
  }

  if (!selectedWarehouse) {
    return (
      <Card>
        <Title level={3}>Cuisine</Title>
        <div style={{ textAlign: "center", padding: "40px 20px" }}>
          <Empty
            image={
              <ExclamationCircleOutlined
                style={{ fontSize: 64, color: "#faad14" }}
              />
            }
            description={
              <div>
                <h3
                  style={{ fontSize: 18, fontWeight: "bold", marginBottom: 8 }}
                >
                  Aucun établissement selectionne
                </h3>
                <p style={{ color: "#666", marginBottom: 16 }}>
                  Selectionnez un établissement pour consulter les tickets cuisine de ce
                  site.
                </p>
              </div>
            }
          />
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className="gestion-produit-container p-sm md:p-md">
        <div
          className="header-section flex flex-col md:flex-row md:justify-between md:items-center gap-md mb-md p-md shadow rounded-lg"
          style={{ backgroundColor: "var(--bg-primary)" }}
        >
          <div className="button-group flex flex-wrap items-center gap-sm">
            <Space size="middle">
              <Button
                type="primary"
                icon={<RocketOutlined />}
                disabled={selectedTicketIds.length === 0}
                onClick={handleOpenKitchenBatch}
              >
                Lancer la selection ({selectedTicketIds.length})
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() =>
                  loadQueue({
                    nextStatus: statusFilter,
                    nextSearch: searchText,
                  })
                }
                loading={loading}
              >
                Rafraichir
              </Button>
              <BusinessAudioToggle
                alertType="kitchen_new_order"
                label="Sons cuisine"
              />
            </Space>
          </div>

          <div className="filter-section flex flex-wrap items-center gap-sm md:gap-md mt-sm md:mt-0">
            <Input
              placeholder="Commande, article, serveur..."
              allowClear
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              className="w-full sm:w-auto md:max-w-xs lg:max-w-sm"
              prefix={<SearchOutlined />}
            />
            <Select
              value={statusFilter}
              onChange={setStatusFilter}
              className="w-full sm:w-auto md:min-w-[180px] lg:min-w-[200px]"
              options={KITCHEN_STATUS_OPTIONS}
            />
          </div>
        </div>

        {isMobileLayout ? (
          loading && tickets.length === 0 ? (
            <Card loading size="small" className="production-kitchen__mobile-card" />
          ) : tickets.length === 0 ? (
            <Empty description="Aucun ticket cuisine ne correspond au filtre courant." />
          ) : (
            <div className="production-kitchen__mobile-list">
              {tickets.map((record) => {
                const canOpen =
                  ticketActions(record).canProduce;

                return (
                  <article
                    key={record.id}
                    className="production-kitchen__mobile-card"
                  >
                    <div className="production-kitchen__mobile-card-head">
                      <div>
                        <div className="production-kitchen__mobile-order-number">
                          {record.order_number || `Ticket #${record.id}`}
                        </div>
                        <div className="production-kitchen__mobile-date">
                          <CalendarOutlined />
                          <span>{formatDateTime(record.order_date)}</span>
                        </div>
                      </div>
                      <div className="production-kitchen__mobile-target-pill">
                        {record.production_unit_name || "Sans unite cible"}
                      </div>
                    </div>

                    <div className="production-kitchen__status-stack">
                      {buildStatusTag(record.line_status === "ready" ? "ready_to_serve" : record.status)}
                    </div>

                    <div className="production-kitchen__mobile-metrics">
                      <div className="production-kitchen__mobile-metric">
                        <span className="production-kitchen__mobile-metric-label">
                          Table
                        </span>
                        <strong>{record.table_label || "Sans table"}</strong>
                      </div>
                      <div className="production-kitchen__mobile-metric">
                        <span className="production-kitchen__mobile-metric-label">
                          Service
                        </span>
                        <strong>{getServiceModeLabel(record.service_mode)}</strong>
                      </div>
                      <div className="production-kitchen__mobile-metric">
                        <span className="production-kitchen__mobile-metric-label">
                          Serveur
                        </span>
                        <strong>{record.waiter_name || "Non renseigne"}</strong>
                      </div>
                      <div className="production-kitchen__mobile-metric">
                        <span className="production-kitchen__mobile-metric-label">
                          Article
                        </span>
                        <strong>{record.product_name || "Produit"}</strong>
                      </div>
                    </div>

                    <div className="production-kitchen__mobile-content">
                      <div className="production-kitchen__mobile-content-head">
                        <span>
                          Demande:{" "}
                          {formatQuantity(
                            record.requested_quantity,
                            record.unit_short_name
                          )}
                        </span>
                        <span>
                          Eligible:{" "}
                          {formatQuantity(
                            record.eligible_quantity,
                            record.unit_short_name
                          )}
                        </span>
                      </div>
                      <span className="production-kitchen__mobile-content-meta">
                        {record.course_label || "Sans course"}
                      </span>
                      {record.line_notes ? (
                        <span className="production-kitchen__mobile-note">
                          Ligne : {record.line_notes}
                        </span>
                      ) : null}
                      {record.resolution_reason ? (
                        <span className="production-kitchen__mobile-note production-kitchen__mobile-note--warning">
                          Motif : {record.resolution_reason}
                        </span>
                      ) : null}
                    </div>

                    <div className="production-kitchen__mobile-actions">
                      <Button
                        size="middle"
                        type={
                          selectedTicketIds.includes(Number(record.id))
                            ? "primary"
                            : "default"
                        }
                        onClick={() => toggleTicketSelection(record.id)}
                        disabled={!canOpen}
                        className="production-kitchen__mobile-action"
                      >
                        {selectedTicketIds.includes(Number(record.id))
                          ? "Selectionne"
                          : "Ajouter au lot"}
                      </Button>
                      {renderKitchenActions(record)}
                    </div>
                  </article>
                );
              })}
            </div>
          )
        ) : (
          <div
            className="overflow-x-auto shadow rounded-lg"
            style={{ backgroundColor: "var(--bg-primary)" }}
          >
            <Table
              rowKey="id"
              loading={loading}
              dataSource={tickets}
              columns={columns}
              rowSelection={{
                selectedRowKeys: selectedTicketIds,
                preserveSelectedRowKeys: false,
                onChange: (keys) => {
                  if (keys.length > KITCHEN_BATCH_MAX_ITEMS) {
                    message.warning(
                      `Un lot est limite a ${KITCHEN_BATCH_MAX_ITEMS} tickets.`,
                    );
                  }
                  setSelectedTicketIds(
                    keys
                      .slice(0, KITCHEN_BATCH_MAX_ITEMS)
                      .map((key) => Number(key)),
                  );
                },
                getCheckboxProps: (record) => ({
                  disabled:
                    !ticketActions(record).canProduce,
                }),
              }}
              expandable={desktopExpandable}
              scroll={{ x: 1480 }}
              pagination={{ pageSize: 20, showSizeChanger: false }}
              locale={{
                emptyText: (
                  <Empty description="Aucun ticket cuisine ne correspond au filtre courant." />
                ),
              }}
            />
          </div>
        )}
      </div>

      {isMobileLayout ? (
        <Drawer
          open={Boolean(detailTicket)}
          onClose={closeDetailModal}
          title={detailTicketTitle}
          placement="bottom"
          height="92vh"
          className="production-kitchen__details-drawer"
        >
          {detailTicketContent}
        </Drawer>
      ) : (
        <Modal
          open={Boolean(detailTicket)}
          onCancel={closeDetailModal}
          footer={null}
          width={920}
          title={detailTicketTitle}
        >
          {detailTicketContent}
        </Modal>
      )}
    </>
  );
};

export default ProductionKitchen;
